import { useState, useEffect, useCallback } from 'react'
import { getMyVacationBalance, getMyVacationRequests } from '@/lib/services/vacation.service'
import type { VacationBalance, VacationRequestsResponse } from '@/types/vacation'

interface UseVacationBalanceResult {
  data: VacationBalance | null
  isLoading: boolean
  error: Error | null
  refetch: () => Promise<void>
  lastUpdated: Date | null
}

/**
 * Custom hook for fetching vacation balance data
 * Implements caching with 5-minute stale time
 * Supports retry on error
 */
export const useVacationBalance = (): UseVacationBalanceResult => {
  const [data, setData] = useState<VacationBalance | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)

  const CACHE_KEY = 'vacation-balance-cache'
  const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes in milliseconds

  const fetchBalance = useCallback(async (forceRefresh = false) => {
    setIsLoading(true)
    setError(null)

    try {
      // Check cache first (unless force refresh)
      if (!forceRefresh) {
        const cached = localStorage.getItem(CACHE_KEY)
        if (cached) {
          const { data: cachedData, timestamp } = JSON.parse(cached)
          const age = Date.now() - timestamp

          if (age < CACHE_DURATION) {
            setData(cachedData)
            setLastUpdated(new Date(timestamp))
            setIsLoading(false)
            return
          }
        }
      }

      // Fetch fresh data
      const response = await getMyVacationBalance()
      setData(response)
      setLastUpdated(new Date())

      // Update cache
      localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({
          data: response,
          timestamp: Date.now()
        })
      )
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error('Failed to fetch vacation balance')
      setError(errorObj)

      // Try to use cached data as fallback
      const cached = localStorage.getItem(CACHE_KEY)
      if (cached) {
        const { data: cachedData, timestamp } = JSON.parse(cached)
        setData(cachedData)
        setLastUpdated(new Date(timestamp))
      }
    } finally {
      setIsLoading(false)
    }
  }, [CACHE_DURATION])

  const refetch = useCallback(async () => {
    await fetchBalance(true)
  }, [fetchBalance])

  useEffect(() => {
    fetchBalance()
  }, [fetchBalance])

  return {
    data,
    isLoading,
    error,
    refetch,
    lastUpdated
  }
}

interface UseVacationRequestsResult {
  data: VacationRequestsResponse | null
  isLoading: boolean
  error: Error | null
  refetch: () => Promise<void>
}

/**
 * Custom hook for fetching vacation requests
 */
export const useVacationRequests = (limit: number = 5): UseVacationRequestsResult => {
  const [data, setData] = useState<VacationRequestsResponse | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)

  const fetchRequests = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await getMyVacationRequests(limit)
      setData(response)
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error('Failed to fetch vacation requests')
      setError(errorObj)
    } finally {
      setIsLoading(false)
    }
  }, [limit])

  const refetch = useCallback(async () => {
    await fetchRequests()
  }, [fetchRequests])

  useEffect(() => {
    fetchRequests()
  }, [fetchRequests])

  return {
    data,
    isLoading,
    error,
    refetch
  }
}
